import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';

/**
 * Initialize native push notifications for iOS/Android.
 * Returns an FCM token if successful, null otherwise.
 */
export async function initializeNativePush(): Promise<string | null> {
  if (!Capacitor.isNativePlatform()) {
    console.log('[Native Push] Not a native platform, skipping initialization');
    return null;
  }

  try {
    const permissionResult = await FirebaseMessaging.requestPermissions();

    if (permissionResult.receive === 'granted') {
      const { token } = await FirebaseMessaging.getToken();
      console.log('[Native Push] FCM token received:', token);

      await registerNativePushToken(token);
      return token;
    } else {
      console.warn('[Native Push] Permission not granted:', permissionResult.receive);
      return null;
    }
  } catch (error) {
    console.error('[Native Push] Error initializing:', error);
    return null;
  }
}

/**
 * Setup push notification listeners for token refresh, foreground
 * notifications, and notification taps.
 */
export async function setupPushListeners(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    // Token refresh — re-register with backend
    await FirebaseMessaging.addListener('tokenReceived', async (event) => {
      console.log('[Native Push] Token refreshed:', event.token);
      await registerNativePushToken(event.token);
    });

    // Foreground notification
    await FirebaseMessaging.addListener('notificationReceived', (event) => {
      console.log('[Native Push] Notification received:', event.notification);
    });

    // User tapped a notification
    await FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
      console.log('[Native Push] Notification action performed:', event);

      const data = event.notification.data as Record<string, string> | undefined;
      if (data?.url) {
        window.location.href = data.url;
      }
    });

    console.log('[Native Push] Listeners setup complete');
  } catch (error) {
    console.error('[Native Push] Error setting up listeners:', error);
  }
}

/**
 * Register an FCM token with the backend via Supabase RPC.
 */
async function registerNativePushToken(token: string): Promise<void> {
  try {
    const { supabase } = await import('@/services/supabase/client');
    const platform = Capacitor.getPlatform();

    const { error } = await (supabase.rpc as any)('register_push_token', {
      p_push_token: token,
      p_device_type: platform,
      p_device_name: await getDeviceName(),
      p_device_id: await getDeviceId(),
      p_user_agent: navigator.userAgent,
      p_browser_name: platform,
      p_browser_version: await getDeviceVersion(),
      p_os_name: platform === 'ios' ? 'iOS' : 'Android',
      p_os_version: await getOSVersion(),
    });

    if (error) {
      console.error('[Native Push] Failed to register token with backend:', error);
    } else {
      console.log('[Native Push] Token successfully registered with backend');
    }
  } catch (error) {
    console.error('[Native Push] Error registering token:', error);
  }
}

// Helper functions to get device information
async function getDeviceName(): Promise<string> {
  try {
    const { Device } = await import('@capacitor/device');
    const info = await Device.getInfo();
    return info.name || info.model || 'Unknown Device';
  } catch {
    return 'Unknown Device';
  }
}

async function getDeviceId(): Promise<string> {
  try {
    const { Device } = await import('@capacitor/device');
    const id = await Device.getId();
    return id.identifier;
  } catch {
    return 'unknown-device-id';
  }
}

async function getDeviceVersion(): Promise<string> {
  try {
    const { Device } = await import('@capacitor/device');
    const info = await Device.getInfo();
    return info.osVersion || 'unknown';
  } catch {
    return 'unknown';
  }
}

async function getOSVersion(): Promise<string> {
  try {
    const { Device } = await import('@capacitor/device');
    const info = await Device.getInfo();
    return info.osVersion || 'unknown';
  } catch {
    return 'unknown';
  }
}
